import { CloseModalButton, SecondaryButton } from '@library/components/buttons';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSecondaryHeader,
  PlayerSubheader
} from '@library/components/player';
import type { ActionMenuOptions } from '@library/types/playerModal';
import { ActionsMenu } from '@library/components/menus';
import { ConfirmRemoveProjectionsModal } from '@bo-components/trading/projections/ConfirmRemoveProjectionsModal';
import { DateTime } from 'luxon';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import { Loading } from '@library/components/Loading';
import { Modal } from '@library/components/modals/Modal';
import type { PlayerDescriptors } from '@library/types/player';
import { PlayerModalStatTable } from '@bo-components/playerModal/PlayerModalStatTable';
import type { SelectOption } from '@library/types/utils';
import { SubmitResetBar } from '@library/components/SubmitResetBar';
import { TabFilter } from '@library/components/filters';
import { classNames } from '@library/utils';
import { count } from 'ramda';
import { isTimeBefore } from '@library/utils/dateTime';
import { useCallback } from 'react';
import { useRemovalWarnings } from '@bo-hooks/playerModal/useRemovalWarnings';

export interface PlayerModalProps {
  actionMenuOptions: ActionMenuOptions;
  changes: Array<Partial<GamePlayerProjectionTrading>>;
  disableActions?: boolean;
  durationId: string;
  durations: Array<SelectOption>;
  errorMessage?: string;
  handleAddProposedProjection: () => void;
  handlePublishProjections: () => void;
  handleResetProjections: () => void;
  isFetching: boolean;
  isOpen: boolean;
  isPublishProjectionsError?: boolean;
  isPublishProjectionsSuccess?: boolean;
  onCancelModal: () => void;
  onDurationChange: (selectedDurationId: string) => void;
  onResetCreateProjections: () => void;
  newProjections: Array<GamePlayerProjectionTrading>;
  updateNewProjection: ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionTrading>;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
  playerDescriptors: PlayerDescriptors;
  playerModalProjectionsData: Array<GamePlayerProjectionTrading>;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionTrading>;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
  statTypes: Array<SelectOption>;
  testId?: string;
  totalChanges: number;
  isDisplayTimeVisible: boolean;
}

export const PlayerModal = ({
  actionMenuOptions,
  changes,
  disableActions = false,
  durationId,
  durations,
  errorMessage = '',
  handlePublishProjections,
  handleResetProjections,
  isFetching,
  isOpen,
  isPublishProjectionsError = false,
  isPublishProjectionsSuccess = false,
  handleAddProposedProjection,
  newProjections,
  onCancelModal,
  onDurationChange,
  onResetCreateProjections,
  playerDescriptors,
  playerModalProjectionsData,
  updateProjection,
  updateNewProjection,
  statTypes,
  testId = 'player-modal',
  totalChanges,
  isDisplayTimeVisible
}: PlayerModalProps) => {
  const handleCancel = () => {
    onCancelModal();
    handleResetProjections();
  };

  const { playerDescription, playerImage, playerName, secondaryHeader } =
    playerDescriptors;

  const showSubmitResetBar = totalChanges > 0;

  const lineScoreErrorCount = count(
    (projection) => !!projection.scoreError,
    playerModalProjectionsData
  );

  const dateErrorCount = count(
    (projection) => !!projection.dateError,
    playerModalProjectionsData
  );

  const saveDisabled =
    playerModalProjectionsData.some((proj) => proj.projectionValue === '') ||
    dateErrorCount > 0;

  const [sampleProjection] = playerModalProjectionsData;
  const { startTime } = sampleProjection ?? {};
  const startTimeIsPast = isTimeBefore({
    dateValue: DateTime.fromISO(startTime)
  });

  const {
    processWarnings,
    removalWarnings,
    handleCancelRemoval,
    handleConfirmRemoval
  } = useRemovalWarnings({
    changes,
    projections: playerModalProjectionsData,
    onNoWarnings: handlePublishProjections
  });

  const handlePublish = useCallback(() => {
    if (!playerModalProjectionsData) return;

    processWarnings();
  }, [playerModalProjectionsData, processWarnings]);

  return (
    <>
      <Modal isOpen={isOpen} onCancelModal={handleCancel} testId={testId}>
        <div className="bg-soFresh-110 border-soFresh-140 relative mx-auto w-[90rem] flex-col rounded-md border">
          <div className="border-soFresh-140 bg-soFresh-130 flex h-24 items-start justify-between rounded-t border-b border-solid">
            <PlayerProfile
              className="grid h-24 grid-cols-[8rem_1fr] overflow-hidden pl-8"
              name={playerName}
              imageUrl={playerImage}
              secondaryHeader={secondaryHeader}
              subheader={playerDescription}
            >
              <PlayerImage className="row-span-2 size-24 object-cover" />
              <div className="flex items-end">
                <PlayerName className="text-soClean-100 text-2xl font-bold" />
                <PlayerSubheader className="text-soClean-140 pb-1 pl-2 text-xs font-medium" />
              </div>

              <PlayerSecondaryHeader className="text-soClean-140 items-start text-xs font-medium" />
            </PlayerProfile>
            <CloseModalButton handleOnClick={handleCancel} />
          </div>
          <div
            className={classNames(
              'no-scrollbar text-soClean-140 min-h-[275px] items-center overflow-y-scroll px-6',
              showSubmitResetBar ? 'h-[33rem]' : 'h-[36.5rem]'
            )}
          >
            <div className="flex flex-row justify-between py-6">
              {durationId && typeof durationId === 'string' && (
                <TabFilter
                  disabled={disableActions}
                  handleSelect={onDurationChange}
                  filterName="duration"
                  options={durations}
                  selectedValue={durationId}
                  testId="duration-tabs"
                />
              )}
              <ActionsMenu
                disabled={disableActions}
                menuDisplay="Actions"
                options={actionMenuOptions}
              />
            </div>
            {isFetching && <Loading loadingMessage="Loading Projections..." />}
            {(playerModalProjectionsData?.length > 0 ||
              newProjections?.length > 0) &&
              !isFetching && (
                <PlayerModalStatTable
                  changes={changes}
                  onResetCreateProjections={onResetCreateProjections}
                  projections={playerModalProjectionsData}
                  newProjections={newProjections}
                  updateNewProjection={updateNewProjection}
                  updateProjection={updateProjection}
                  statTypes={statTypes}
                  isDisplayTimeVisible={isDisplayTimeVisible}
                />
              )}
            <div className="py-6">
              <SecondaryButton
                disabled={startTimeIsPast}
                handleOnClick={handleAddProposedProjection}
                title="Add Stat"
              >
                + Add Stat
              </SecondaryButton>
            </div>
          </div>
          {showSubmitResetBar && (
            <SubmitResetBar
              cancelText="Reset Projections"
              handlePublish={handlePublish}
              handleReset={handleResetProjections}
              prefix={
                <>
                  {lineScoreErrorCount > 0 && (
                    <span className="text-roses font-semibold">{`${lineScoreErrorCount} Projection(s) out of range!`}</span>
                  )}
                  {dateErrorCount > 0 && (
                    <span className="text-roses font-semibold">{`${dateErrorCount} Projection(s) have invalid board times!`}</span>
                  )}
                </>
              }
              saveDisabled={saveDisabled}
            />
          )}
          {isPublishProjectionsError && (
            <p className="text-roses p-6">{`Error: ${errorMessage ?? ''}`}</p>
          )}
          {isPublishProjectionsSuccess && (
            <p className="text-atlien-200 p-6">
              Projections Published Successfully
            </p>
          )}
        </div>
      </Modal>
      <ConfirmRemoveProjectionsModal
        removalWarnings={removalWarnings}
        handleCancelRemoval={handleCancelRemoval}
        handleConfirmRemoval={handleConfirmRemoval}
      />
    </>
  );
};
